import React, { ForwardedRef, forwardRef } from "react";
import {
  isCanvasPosInitialized,
  useCanvasPosRealtime,
} from "@/store/zustand/canvasStore";
import { CANVAS_HEIGHT, CANVAS_WIDTH } from "./utils";

interface ScalablePanningLayerProps {
  children: React.ReactNode;
  onMouseDown?: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
  onMouseUp?: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
  onMouseLeave?: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
  cursorStyle: string;
}

// Use forwardRef to allow the parent component (Canvas.tsx) to pass a ref to the underlying div
export const ScalablePanningLayer = React.memo(
  forwardRef<HTMLDivElement, ScalablePanningLayerProps>(
    ({ children, onMouseDown, onMouseUp, onMouseLeave, cursorStyle }, ref) => {
      const canvasPos = useCanvasPosRealtime();
      const { zoomPerc, transformX, transformY } = isCanvasPosInitialized(
        canvasPos
      )
        ? canvasPos
        : {
            zoomPerc: 1,
            transformX: 0,
            transformY: 0,
          };

      const style: React.CSSProperties = {
        position: "absolute", // Added position absolute
        left: `calc(-${CANVAS_WIDTH / 2}px - ${
          transformX * zoomPerc * 0.45
        }px + 50%)`,
        top: `calc(-${CANVAS_HEIGHT / 2}px - ${
          transformY * zoomPerc * 0.45
        }px + 50%)`,
        width: CANVAS_WIDTH,
        height: CANVAS_HEIGHT,
        transform: zoomPerc ? `scale(${zoomPerc * 0.45})` : "",
        cursor: cursorStyle,
        overflow: "hidden", // Added overflow hidden
      };

      return (
        <div
          ref={ref} // Attach the forwarded ref here
          className={"absolute top-0 left-0"} // className from original div in Canvas.tsx
          style={style}
          onMouseDown={onMouseDown}
          onMouseUp={onMouseUp}
          onMouseLeave={onMouseLeave}
        >
          {children}
        </div>
      );
    }
  )
);

ScalablePanningLayer.displayName = "ScalablePanningLayer";
